import { copy, fromBase64Url, toBase64Url } from './bytes.js';
import { sha256 } from './hash.js';
import { canonicalize } from './jcs.js';
const subtle = () => globalThis.crypto.subtle;
/** COSE algorithm identifiers we accept for passkeys. */
export const COSE_ALG = { ES256: -7, EdDSA: -8, Ed25519: -19, RS256: -257 };
const ECDSA_P256 = { name: 'ECDSA', namedCurve: 'P-256' };
const ECDSA_SHA256 = { name: 'ECDSA', hash: 'SHA-256' };
/**
 * Generate a P-256 signing key pair. Browser device keys MUST keep the default
 * `extractable: false`; the public half is always exportable regardless.
 */
export async function generateP256KeyPair(options = {}) {
    const pair = (await subtle().generateKey(ECDSA_P256, options.extractable ?? false, [
        'sign',
        'verify',
    ]));
    return { publicKey: pair.publicKey, privateKey: pair.privateKey };
}
/** Sign with ECDSA P-256/SHA-256. Returns the raw 64-byte r||s (IEEE P1363) signature. */
export async function signP256(privateKey, data) {
    return new Uint8Array(await subtle().sign(ECDSA_SHA256, privateKey, copy(data)));
}
/** Verify a raw r||s P-256 signature. Never throws; malformed input is `false`. */
export async function verifyP256(publicKey, signature, data) {
    if (signature.length !== 64)
        return false;
    try {
        return await subtle().verify(ECDSA_SHA256, publicKey, copy(signature), copy(data));
    }
    catch {
        return false;
    }
}
export function isEcPublicJwk(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    return v.kty === 'EC' && v.crv === 'P-256' && typeof v.x === 'string' && typeof v.y === 'string';
}
export async function exportP256PublicJwk(publicKey) {
    const jwk = (await subtle().exportKey('jwk', publicKey));
    if (!isEcPublicJwk(jwk))
        throw new Error('not a P-256 public key');
    return { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y };
}
/** Import a P-256 public JWK for verification (validates the point is on the curve). */
export async function importP256PublicJwk(jwk) {
    return importPublicJwk(jwk, COSE_ALG.ES256);
}
/** Import a public JWK for the given COSE algorithm, ready for `verify`. */
export async function importPublicJwk(jwk, alg) {
    const s = subtle();
    switch (alg) {
        case COSE_ALG.ES256:
            if (jwk.kty !== 'EC')
                throw new Error('ES256 requires an EC key');
            assertLength(jwk.x, 32);
            assertLength(jwk.y, 32);
            return s.importKey('jwk', { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y }, ECDSA_P256, true, [
                'verify',
            ]);
        case COSE_ALG.EdDSA:
        case COSE_ALG.Ed25519:
            if (jwk.kty !== 'OKP')
                throw new Error('EdDSA requires an OKP key');
            assertLength(jwk.x, 32);
            return s.importKey('jwk', { kty: 'OKP', crv: 'Ed25519', x: jwk.x }, { name: 'Ed25519' }, true, ['verify']);
        case COSE_ALG.RS256:
            if (jwk.kty !== 'RSA')
                throw new Error('RS256 requires an RSA key');
            if (fromBase64Url(jwk.n).length < 256)
                throw new Error('RSA modulus must be >= 2048 bits');
            return s.importKey('jwk', { kty: 'RSA', n: jwk.n, e: jwk.e }, { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, true, ['verify']);
        default:
            throw new Error(`unsupported COSE alg ${String(alg)}`);
    }
}
function assertLength(b64, length) {
    if (fromBase64Url(b64).length !== length)
        throw new Error('invalid key coordinate length');
}
/** DER-encoded SubjectPublicKeyInfo for a public JWK. */
export async function jwkToSpki(jwk, alg) {
    const key = await importPublicJwk(jwk, alg);
    return new Uint8Array(await subtle().exportKey('spki', key));
}
/** RFC 7638 JWK thumbprint (SHA-256, base64url). Used as device and daemon key ids. */
export async function jwkThumbprint(jwk) {
    let members;
    switch (jwk.kty) {
        case 'EC':
            members = { crv: jwk.crv, kty: jwk.kty, x: jwk.x, y: jwk.y };
            break;
        case 'OKP':
            members = { crv: jwk.crv, kty: jwk.kty, x: jwk.x };
            break;
        case 'RSA':
            members = { e: jwk.e, kty: jwk.kty, n: jwk.n };
            break;
    }
    return toBase64Url(await sha256(new TextEncoder().encode(canonicalize(members))));
}
// ---- ECDSA signature encodings ------------------------------------------------------------
/** Convert an ASN.1 DER `SEQUENCE { INTEGER r, INTEGER s }` to raw r||s. Throws if malformed. */
export function derToRawEcdsa(der, size = 32) {
    let o = 0;
    const byte = () => {
        if (o >= der.length)
            throw new Error('DER: truncated');
        return der[o++];
    };
    const readLength = () => {
        const first = byte();
        if (first < 0x80)
            return first;
        if (first === 0x81) {
            const len = byte();
            if (len < 0x80)
                throw new Error('DER: non-minimal length');
            return len;
        }
        throw new Error('DER: unsupported length');
    };
    const readInt = () => {
        if (byte() !== 0x02)
            throw new Error('DER: expected INTEGER');
        const len = readLength();
        if (len === 0 || o + len > der.length)
            throw new Error('DER: bad INTEGER length');
        let int = der.subarray(o, o + len);
        o += len;
        if (int[0] & 0x80)
            throw new Error('DER: negative INTEGER');
        if (int.length > 1 && int[0] === 0) {
            if (!(int[1] & 0x80))
                throw new Error('DER: non-minimal INTEGER');
            int = int.subarray(1);
        }
        if (int.length > size)
            throw new Error('DER: INTEGER too large');
        return int;
    };
    if (byte() !== 0x30)
        throw new Error('DER: expected SEQUENCE');
    const seqLen = readLength();
    if (o + seqLen !== der.length)
        throw new Error('DER: bad SEQUENCE length');
    const r = readInt();
    const s = readInt();
    if (o !== der.length)
        throw new Error('DER: trailing bytes');
    const out = new Uint8Array(size * 2);
    out.set(r, size - r.length);
    out.set(s, size * 2 - s.length);
    return out;
}
/** Convert raw r||s to ASN.1 DER (what authenticators, Secure Enclave and TPMs emit). */
export function rawToDerEcdsa(raw) {
    if (raw.length % 2 !== 0)
        throw new Error('raw signature must have even length');
    const half = raw.length / 2;
    const encodeInt = (bytes) => {
        let i = 0;
        while (i < bytes.length - 1 && bytes[i] === 0)
            i++;
        const trimmed = Array.from(bytes.subarray(i));
        if (trimmed[0] & 0x80)
            trimmed.unshift(0);
        return [0x02, trimmed.length, ...trimmed];
    };
    const body = [...encodeInt(raw.subarray(0, half)), ...encodeInt(raw.subarray(half))];
    const header = body.length < 0x80 ? [0x30, body.length] : [0x30, 0x81, body.length];
    return new Uint8Array([...header, ...body]);
}
//# sourceMappingURL=keys.js.map